import store from "../store";
import { loginResponse, logoutRequest } from "../store/Auth/actions";
import { companyLogout } from "../network/apis/companyPortalAuth";
import { clearFirebaseToken, generateFirebaseToken } from "../firebase/helpers";
import history from "../routes/History";
import {
  clearAllPortalLoginSnapshots,
  readPersistedLoginForPortal,
  SESSION_PORTAL_KEY,
} from "./authStorage";
import {
  fetchCompanyPortalCurrentUser,
  flattenCompanyPortalResource,
} from "../network/apis/companyPortalRegistration";
import { clearAdminData, getDeviceId, setAdmin } from "./Auth";
import { ROUTE_PATHS } from "./RoutesPaths";
import { companyPermissionModules } from "./Constants";
import {
  canCompanyModule,
  getCompanyPortalHomeRoute,
} from "./Permissions";

/** Flatten nested JSON:API `company` / `company_role` on portal session payloads. */
export function normalizeCompanyPortalSessionUser(user) {
  if (!user || typeof user !== "object") return user;
  const next = { ...user };
  if (next.company) {
    next.company = flattenCompanyPortalResource(next.company);
  }
  if (next.company_role) {
    next.company_role = flattenCompanyPortalResource(next.company_role);
  }
  if (next.role) {
    next.role = flattenCompanyPortalResource(next.role);
  }
  return next;
}

function redirectCompanyPortalAwayFromDeniedRoutes(login) {
  if (localStorage.getItem(SESSION_PORTAL_KEY) !== "company") return;

  const path = history.location?.pathname ?? "";
  const onEvents =
    path === ROUTE_PATHS.companyEvents ||
    path.startsWith(`${ROUTE_PATHS.companyEvents}/`);

  if (
    onEvents &&
    !canCompanyModule(companyPermissionModules.events, "can_view", login)
  ) {
    history.replace(getCompanyPortalHomeRoute(login));
  }
}

/**
 * Base query for GET `/cities` on the company portal (registered city dropdown).
 * Send `name` only when the user searches. `page`: e.g. `-1` for all.
 */
export const COMPANY_SIGNUP_REGISTERED_CITIES_QUERY = {
  page: -1,
};

/**
 * Pure check on login/registration payload (e.g. login API `data`).
 */
export function isCompanyRegistrationIncompleteState(data) {
  if (!data?.auth_token || !data.profile_status) return false;
  const ps = data.profile_status;
  if (ps === "unverified") return true;
  if (ps === "verified") {
    if (data.company == null || data.company === undefined) return true;
    if (data.company.status === "pending") return true;
  }
  return false;
}

/**
 * True when the stored company-portal session must finish signup (layout + private routes).
 */
export function mustCompleteCompanyRegistration() {
  if (localStorage.getItem("sessionPortal") !== "company") return false;
  const admin = getResolvedCompanyPortalSession();
  if (!admin?.auth_token) return false;
  return isCompanyRegistrationIncompleteState(admin);
}

/**
 * Merges stored admin with latest Redux login (avoids stale reads after dispatch).
 */
export function getResolvedCompanyPortalSession() {
  let a = readPersistedLoginForPortal("company");
  const fromRedux = store.getState().auth.login;
  if (fromRedux && fromRedux.auth_token) {
    a = { ...a, ...fromRedux };
  }
  return a;
}

/**
 * `can_resend_activation_code` from company user payloads (POST /company_users,
 * POST /company_users/resend_email_verification). Prefer the **persisted** snapshot:
 * Redux merged from POST /sessions can omit or override this field after
 * `persistCompanyPortalSession` wrote the value from registration/resend.
 */
export function getCompanyPortalCanResendActivationCode() {
  const persisted = readPersistedLoginForPortal("company");
  const merged = getResolvedCompanyPortalSession();

  const pick = (obj) => {
    if (obj == null || typeof obj !== "object") return undefined;
    const top = obj.can_resend_activation_code;
    if (top === true || top === false) return top;
    const nested = obj.attributes?.can_resend_activation_code;
    if (nested === true || nested === false) return nested;
    return undefined;
  };

  const fromPersisted = pick(persisted);
  const fromMerged = pick(merged);

  if (fromPersisted === true || fromPersisted === false) return fromPersisted;
  if (fromMerged === true || fromMerged === false) return fromMerged;
  return fromMerged !== false && fromPersisted !== false;
}

/** `company.status` on a login, `me` or `my_company` payload (PROLINE-4124). */
export const isCompanyDeactivated = (login) =>
  login?.company?.status === "deactivated";

/**
 * A deactivated company keeps no portal session, only the blocked screen. The API answers its
 * logout with 403, so the session ends on this side only.
 */
export function leaveDeactivatedCompanySession() {
  store.dispatch(loginResponse({}));
  clearAdminData();
  clearFirebaseToken();
  generateFirebaseToken();
  history.replace(ROUTE_PATHS.companyDeactivated);
}

let companyStatusRecheck = null;

/**
 * A 403 on the company portal may mean the company was just deactivated: re-reads `me` and, if so,
 * leaves the session. Resolves to whether it left; 403s arriving together share one request.
 */
export function recheckCompanyDeactivated() {
  if (!companyStatusRecheck) {
    companyStatusRecheck = fetchCompanyPortalCurrentUser({ handlerEnabled: false })
      .then((me) => {
        if (!isCompanyDeactivated(me)) return false;
        leaveDeactivatedCompanySession();
        return true;
      })
      .catch(() => false)
      .finally(() => {
        companyStatusRecheck = null;
      });
  }
  return companyStatusRecheck;
}

export function persistCompanyPortalSession(partial) {
  const base = getResolvedCompanyPortalSession();
  const next = normalizeCompanyPortalSessionUser({ ...base, ...partial });
  if (isCompanyDeactivated(next)) {
    leaveDeactivatedCompanySession();
    return;
  }
  setAdmin(next);
  store.dispatch(loginResponse(next));
  redirectCompanyPortalAwayFromDeniedRoutes(next);
}

/**
 * Target route for the user's current registration state under `/company/signup`.
 * Without token → onboarding. Legacy user (no profile_status) with token → main app.
 */
export function getCompanyRegistrationStepRoute(adminFromSaga) {
  const a =
    adminFromSaga && adminFromSaga.auth_token
      ? adminFromSaga
      : getResolvedCompanyPortalSession();
  if (!a?.auth_token) return ROUTE_PATHS.companySignup;
  if (!a.profile_status) return ROUTE_PATHS.companyHome;
  if (a.profile_status === "unverified") return ROUTE_PATHS.companySignupOtp;
  if (
    a.profile_status === "verified" &&
    (a.company == null || a.company === undefined)
  ) {
    return ROUTE_PATHS.companySignupCompany;
  }
  if (a.profile_status === "verified" && a.company?.status === "pending") {
    return ROUTE_PATHS.companyPendingApproval;
  }
  return ROUTE_PATHS.companyHome;
}

/**
 * Set on any company-portal login while `must_change_password`; cleared after successful change.
 */
export const PENDING_COMPANY_RESET_PASSWORD_FLOW_KEY =
  "pending_company_reset_password_flow";

/** Company portal session must set a new password before any other navigation. */
export function isCompanyMustChangePasswordLocked(admin) {
  if (localStorage.getItem("sessionPortal") !== "company") return false;
  const a =
    admin && admin.auth_token ? admin : getResolvedCompanyPortalSession();
  return Boolean(a?.must_change_password);
}

/** Routes permitted while `must_change_password` (auth reset flow only — not in-portal settings). */
export function isAllowedPathWhileCompanyMustChangePassword(pathname) {
  return (
    isUnderCompanyResetPasswordPath(pathname) ||
    pathname === ROUTE_PATHS.companyChangePassword
  );
}

/**
 * Where to send the user when they hit a disallowed URL with `must_change_password`.
 * Step 2 of `/company/reset-password` (auth layout — not the portal settings screen).
 */
export function getCompanyMustChangePasswordLockRedirect(admin) {
  const a =
    admin && admin.auth_token ? admin : getResolvedCompanyPortalSession();
  return {
    pathname: ROUTE_PATHS.companyResetPassword,
    state: getCompanyResetPasswordLockRedirectState(a),
  };
}

export function isUnderCompanyResetPasswordPath(pathname) {
  return (
    typeof pathname === "string" &&
    pathname.startsWith(ROUTE_PATHS.companyResetPassword)
  );
}

/** Exact `/company/reset-password` only (not `/success`, etc.) — step 1 + 2 share this URL. */
export function isCompanyResetPasswordFlowBasePath(pathname) {
  if (typeof pathname !== "string") return false;
  const collapsed = pathname.replace(/\/+/g, "/");
  const p =
    collapsed.length > 1 && collapsed.endsWith("/")
      ? collapsed.slice(0, -1)
      : collapsed;
  return p === ROUTE_PATHS.companyResetPassword;
}

export function getCompanyResetPasswordLockRedirectState(admin) {
  const a =
    admin && admin.auth_token ? admin : getResolvedCompanyPortalSession();
  return {
    advanceToStep2: true,
    email: typeof a?.email === "string" ? a.email : "",
    resetPasswordFormKey: Date.now(),
  };
}

export function isCompanySignupPath(pathname) {
  return (
    typeof pathname === "string" &&
    pathname.startsWith(ROUTE_PATHS.companySignup)
  );
}

/** Collapses duplicate slashes so `/company/signup//contact` matches defined routes. */
export function normalizeCompanySignupPathname(pathname) {
  if (typeof pathname !== "string") return "";
  const collapsed = pathname.replace(/\/+/g, "/");
  return collapsed.length > 1 && collapsed.endsWith("/")
    ? collapsed.slice(0, -1)
    : collapsed;
}

/** Welcome/onboarding screen at `/company/signup` — AuthLayout `onboarding` variant. */
export function isCompanySignupOnboardingPath(pathname) {
  return (
    normalizeCompanySignupPathname(pathname) === ROUTE_PATHS.companySignup
  );
}

/**
 * True if the user may stay on this signup URL without being redirected to the canonical step.
 * Blocks skipping ahead (e.g. company form before email is verified) but allows moving back
 * to earlier steps (e.g. contact while still unverified).
 */
export function isAllowedCompanySignupStepPath(pathname, admin) {
  const path = normalizeCompanySignupPathname(pathname);
  const a =
    admin && admin.auth_token ? admin : getResolvedCompanyPortalSession();

  const root = ROUTE_PATHS.companySignup;
  const isSignupRoot = path === root;

  if (!path.startsWith(root)) {
    return true;
  }

  if (!a?.auth_token) {
    return isSignupRoot || path === ROUTE_PATHS.companySignupContact;
  }

  if (!a.profile_status) {
    return true;
  }

  if (a.profile_status === "unverified") {
    return (
      path === ROUTE_PATHS.companySignupContact ||
      path === ROUTE_PATHS.companySignupOtp
    );
  }

  if (
    a.profile_status === "verified" &&
    (a.company == null || a.company === undefined)
  ) {
    return (
      path === ROUTE_PATHS.companySignupContact ||
      path === ROUTE_PATHS.companySignupCompany
    );
  }

  if (a.profile_status === "verified" && a.company?.status === "pending") {
    return false;
  }

  return true;
}

export function normalizeCompanyLoginPathname(pathname) {
  if (typeof pathname !== "string") return "";
  const collapsed = pathname.replace(/\/+/g, "/");
  return collapsed.length > 1 && collapsed.endsWith("/")
    ? collapsed.slice(0, -1)
    : collapsed;
}

export function isCompanyLoginPath(pathname) {
  return (
    typeof pathname === "string" &&
    (pathname === ROUTE_PATHS.companyLogin ||
      pathname.startsWith(`${ROUTE_PATHS.companyLogin}/`))
  );
}

/**
 * Next route after company-portal sessions#create — uses `/company/login/*`, not signup URLs.
 */
export function getCompanyLoginPostAuthRoute(adminFromSaga) {
  const a =
    adminFromSaga && adminFromSaga.auth_token
      ? adminFromSaga
      : getResolvedCompanyPortalSession();
  if (!a?.auth_token) return ROUTE_PATHS.companyLogin;
  if (!a.profile_status) return ROUTE_PATHS.companyHome;
  if (a.profile_status === "unverified") return ROUTE_PATHS.companyLoginOtp;
  if (
    a.profile_status === "verified" &&
    (a.company == null || a.company === undefined)
  ) {
    return ROUTE_PATHS.companyLoginCompany;
  }
  if (a.profile_status === "verified" && a.company?.status === "pending") {
    return ROUTE_PATHS.companyPendingApproval;
  }
  return ROUTE_PATHS.companyHome;
}

export function getCompanyLoginCanonicalStepRoute() {
  return getCompanyLoginPostAuthRoute();
}

export function isAllowedCompanyLoginStepPath(pathname, admin) {
  const path = normalizeCompanyLoginPathname(pathname);
  const a =
    admin && admin.auth_token ? admin : getResolvedCompanyPortalSession();
  const root = ROUTE_PATHS.companyLogin;

  if (!isCompanyLoginPath(path)) {
    return true;
  }

  if (!a?.auth_token) {
    return path === root;
  }

  if (!a.profile_status) {
    return true;
  }

  if (a.profile_status === "unverified") {
    return path === root || path === ROUTE_PATHS.companyLoginOtp;
  }

  if (
    a.profile_status === "verified" &&
    (a.company == null || a.company === undefined)
  ) {
    return path === root || path === ROUTE_PATHS.companyLoginCompany;
  }

  if (a.profile_status === "verified" && a.company?.status === "pending") {
    return false;
  }

  return true;
}

/**
 * Ends the company-portal session via DELETE /company_portal/v1/sessions (Bearer),
 * then clears local storage and navigates to company login (see `logoutSaga`).
 */
export function requestCompanyPortalLogout() {
  store.dispatch(
    logoutRequest({
      device_id: getDeviceId(),
    })
  );
}

/**
 * Pending-approval header logout: DELETE company-portal session, then clear client state
 * even if the request fails (per product spec).
 */
export async function performCompanyPendingApprovalLogout() {
  try {
    await companyLogout({ device_id: getDeviceId() });
  } catch {
    /* still clear client */
  }
  // Clear every portal snapshot: any leftover blob would be read back as the active
  // session once `sessionPortal` is gone (bucket lookup defaults to admin).
  clearAllPortalLoginSnapshots();
  localStorage.removeItem(SESSION_PORTAL_KEY);
  sessionStorage.removeItem(SESSION_PORTAL_KEY);
  store.dispatch(loginResponse({}));
  clearFirebaseToken();
  generateFirebaseToken();
  history.replace(ROUTE_PATHS.companyLogin);
}
